import type { JsonLd } from '@atlaskit/json-ld-types/jsonld';
import {
	extractPersonAssignedTo,
	extractPersonOwnedBy,
	extractSmartLinkAri,
	extractSmartLinkCreatedBy,
	extractSmartLinkCreatedOn,
	extractSmartLinkModifiedBy,
	extractSmartLinkModifiedOn,
	extractSmartLinkProvider,
	extractSmartLinkTitle,
	extractType,
	extractUrlFromIconJsonLd,
} from '@atlaskit/link-extractors';

import type { AuthService, EnhancedSmartLink } from './smartLinkTypes.ts';
import { extractStateFromJsonLd } from './extractStateFromJsonLd.ts';
import { buildImageIcon, extractSmartLinkIcon } from './smartLinkIcon.ts';
import { isTownsquareProduct } from './townsquare.ts';

const DISPLAY_NAME_PREFIX_REGEX = /^Atlassian(?:\s+(?:Links|Connector))?\s*-\s*/i;

const stripDisplayNamePrefix = (displayName: string): string =>
	displayName.replace(DISPLAY_NAME_PREFIX_REGEX, '').trim();

/**
 * Converts a raw JsonLd.Response (from ORS or the SmartCard store) into an
 * EnhancedSmartLink, preserving the url and occurrenceIndex from the ADF walk.
 *
 * Optional fields are undefined when the provider does not supply them.
 */
export function jsonLdToEnhancedSmartLink(
	url: string,
	occurrenceIndex: number,
	response: JsonLd.Response | null | undefined,
	normalizeShallowJira = false,
): EnhancedSmartLink {
	const resp = response ?? undefined;
	const data = resp?.data as JsonLd.Data.BaseData | undefined;
	const meta = resp?.meta as (JsonLd.Meta.BaseMeta & Record<string, unknown>) | undefined;

	// The ORS batch resolver ("Referenced in") can return a shallow Jira payload — the raw Jira REST
	// issue (no top-level `data.icon`, with the issue type under `data.fields.issuetype`). Read the
	// icon and type from there. Gated so the hydrated "Links to" path is untouched.
	const issuetype =
		normalizeShallowJira && !data?.icon
			? (data as { fields?: { issuetype?: { name?: string; iconUrl?: string } } } | undefined)
					?.fields?.issuetype
			: undefined;
	const shallowJiraIssuetype = issuetype?.iconUrl ? issuetype : undefined;

	// ── identity ──────────────────────────────────────────────────────────────
	const ari = data ? extractSmartLinkAri(resp) : undefined;

	// ── display ───────────────────────────────────────────────────────────────
	const title = data ? extractSmartLinkTitle(resp) : undefined;
	const iconUrl = data?.icon ? extractUrlFromIconJsonLd(data.icon) : shallowJiraIssuetype?.iconUrl;

	// ── classification ────────────────────────────────────────────────────────
	const key = meta?.key != null ? String(meta.key) : undefined;
	const metaProduct = meta?.product != null ? String(meta.product) : undefined;
	const subproduct = meta?.subproduct != null ? String(meta.subproduct) : undefined;
	const resourceTypeRaw = meta?.resourceSubType ?? shallowJiraIssuetype?.name ?? meta?.resourceType;
	const fallbackResourceType = resourceTypeRaw != null ? String(resourceTypeRaw) : undefined;
	// The JSON-LD generator names the app that produced the object (e.g. "Focus",
	// "Loom"). It's the product-name fallback when `meta.product` is absent, and
	// the per-row app label for the collapsed townsquare/"Goals" group. Skip the
	// bare-string generator — extractSmartLinkProvider (via extractProvider)
	// throws on it rather than returning undefined.
	const generatorName =
		data != null && typeof data.generator !== 'string'
			? extractSmartLinkProvider(resp)?.text
			: undefined;
	// Atlas Projects/Goals arrive as the `townsquare` codename and collapse under
	// the "Goals" group; surface the specific app (generator name) as the row's
	// resource type so the metadata line shows the real product.
	const isTownsquare = isTownsquareProduct(metaProduct);
	const resourceType = isTownsquare
		? (generatorName ?? fallbackResourceType)
		: fallbackResourceType;
	const jsonLdType = data ? extractType(data)?.[0] : undefined;

	// ── people ────────────────────────────────────────────
	const owner = data ? extractPersonOwnedBy(data)?.[0]?.name : undefined;
	const assignedTo = data ? extractPersonAssignedTo(data as JsonLd.Data.Task)?.name : undefined;
	const createdBy = data ? extractSmartLinkCreatedBy(resp) : undefined;
	const updatedBy = data ? extractSmartLinkModifiedBy(resp) : undefined;

	// ── dates ─────────────────────────────────────────────────────────────────
	const createdAt = data ? extractSmartLinkCreatedOn(resp) : undefined;
	const updatedAt = data ? extractSmartLinkModifiedOn(resp) : undefined;

	// ── status ────────────────────────────────────────────────────────────────
	const taskStatusRaw = (data as JsonLd.Data.Task)?.['atlassian:taskStatus'];
	const taskStatus = taskStatusRaw?.name != null ? String(taskStatusRaw.name) : undefined;

	const state = extractStateFromJsonLd((data as any)?.['atlassian:state']);

	// ── auth ──────────────────────────────────────────────────────────────────
	const access = meta?.access as EnhancedSmartLink['access'];
	const visibility = meta?.visibility as EnhancedSmartLink['visibility'];
	const authProviders: AuthService[] = Array.isArray(meta?.auth)
		? (meta.auth as AuthService[]).filter(
				(a): a is AuthService =>
					typeof a === 'object' &&
					typeof a.displayName === 'string' &&
					typeof a.key === 'string' &&
					typeof a.url === 'string',
			)
		: [];
	const providerDisplayName = authProviders[0]?.displayName;
	const productFromAuthProvider =
		typeof providerDisplayName === 'string' && providerDisplayName.length > 0
			? stripDisplayNamePrefix(providerDisplayName)
			: undefined;
	// `meta.product` wins; else the connected provider's display name (unauthorized
	// 3P links); else the JSON-LD generator name (Focus, Loom, …) — which is how
	// product-less 1P links get classified instead of falling into "Other".
	const resolvedProduct = metaProduct ?? productFromAuthProvider ?? generatorName;
	const iconSource = { ari, occurrenceIndex, product: resolvedProduct, url };
	const icon = shallowJiraIssuetype?.iconUrl
		? buildImageIcon(shallowJiraIssuetype.iconUrl, iconSource)
		: extractSmartLinkIcon(resp, iconSource);

	return {
		url,
		occurrenceIndex,
		ari,
		title,
		icon,
		iconUrl,
		product: resolvedProduct,
		key,
		subproduct,
		resourceType,
		jsonLdType,
		owner,
		assignedTo,
		createdBy,
		updatedBy,
		createdAt,
		updatedAt,
		taskStatus,
		state,
		access,
		visibility,
		authProviders,
	};
}
